import React, { useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Slide deck data structure
interface SlideDeck {
  id: number;
  title: string;
  description: string;
  pdfUrl: string;
  totalSlides: number;
  thumbnail?: string;
}

export default function SlideViewer() {
  // Slide decks collection - easily add more decks here
  const decks: SlideDeck[] = [
    {
      id: 1,
      title: "Auditable AI Trust Playbook",
      description: "Constitutional AI Engineering Framework",
      pdfUrl: "https://raw.githubusercontent.com/albertorocha329/public-slides-/main/Auditable_AI_Trust_Playbook.pdf",
      totalSlides: 12
    },
    {
      id: 2,
      title: "The Digital Echo Accountability",
      description: "A deep dive into how AI echoes human biases and the frameworks needed for accountability.",
      pdfUrl: "https://raw.githubusercontent.com/albertorocha329/public-slides-/main/The_Digital_Echo_Accountability.pdf",
      totalSlides: 13
    }
  ];

  const [currentDeckIndex, setCurrentDeckIndex] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  
  const currentDeck = decks[currentDeckIndex];

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setLoading(false);
  };

  const nextSlide = () => {
    if (currentSlide < currentDeck.totalSlides) {
      setCurrentSlide(prev => prev + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 1) {
      setCurrentSlide(prev => prev - 1);
    }
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const nextDeck = () => {
    if (currentDeckIndex < decks.length - 1) {
      setCurrentDeckIndex(prev => prev + 1);
      setCurrentSlide(1);
      setLoading(true);
    }
  };

  const prevDeck = () => {
    if (currentDeckIndex > 0) {
      setCurrentDeckIndex(prev => prev - 1);
      setCurrentSlide(1);
      setLoading(true);
    }
  };

  const goToDeck = (index: number) => {
    setCurrentDeckIndex(index);
    setCurrentSlide(1);
    setLoading(true);
  };

  // Keyboard navigation
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        nextSlide();
      } else if (e.key === 'ArrowLeft') {
        prevSlide();
      } else if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentSlide, currentDeck.totalSlides, isFullscreen]);

  return (
    <section className="py-24 bg-gradient-to-br from-slate-50 to-white">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center w-16 h-16 bg-teal-600 rounded-lg mx-auto mb-6">
            <i className="ri-slideshow-line text-3xl text-white"></i>
          </div>
          <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
            Interactive Slide Decks
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Explore our comprehensive presentations and frameworks
          </p>
        </div>

        {/* Deck Carousel Navigation */}
        {decks.length > 1 && (
          <div className="max-w-6xl mx-auto mb-8">
            <div className="flex items-center justify-between gap-4">
              <button
                onClick={prevDeck}
                disabled={currentDeckIndex === 0}
                className="p-3 bg-white border-2 border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 hover:border-teal-600 disabled:opacity-30 disabled:cursor-not-allowed transition-all flex items-center justify-center w-12 h-12 cursor-pointer"
                aria-label="Previous deck"
              >
                <i className="ri-arrow-left-s-line text-2xl"></i>
              </button>

              {/* Deck Indicators */}
              <div className="flex items-center gap-3 flex-wrap justify-center">
                {decks.map((deck, index) => (
                  <button
                    key={deck.id}
                    onClick={() => goToDeck(index)}
                    className={`px-4 py-2 rounded-lg font-medium transition-all cursor-pointer ${
                      index === currentDeckIndex
                        ? 'bg-teal-600 text-white shadow-lg'
                        : 'bg-white text-slate-600 border border-slate-200 hover:border-teal-600 hover:text-teal-600'
                    }`}
                  >
                    {index + 1}
                  </button>
                ))}
              </div>

              <button
                onClick={nextDeck}
                disabled={currentDeckIndex === decks.length - 1}
                className="p-3 bg-white border-2 border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 hover:border-teal-600 disabled:opacity-30 disabled:cursor-not-allowed transition-all flex items-center justify-center w-12 h-12 cursor-pointer"
                aria-label="Next deck"
              >
                <i className="ri-arrow-right-s-line text-2xl"></i>
              </button>
            </div>

            {/* Current Deck Info */}
            <div className="text-center mt-6">
              <h3 className="text-2xl font-bold text-slate-900 mb-2">
                {currentDeck.title}
              </h3>
              <p className="text-slate-600">
                {currentDeck.description}
              </p>
              <p className="text-sm text-slate-500 mt-2">
                Deck {currentDeckIndex + 1} of {decks.length}
              </p>
            </div>
          </div>
        )}

        <div 
          className={`max-w-6xl mx-auto ${isFullscreen ? 'fixed inset-0 z-50 bg-black/95 p-8' : ''}`}
          tabIndex={0}
        >
          {/* Slide Viewer */}
          <div className="relative bg-white rounded-lg shadow-2xl overflow-hidden">
            {/* PDF Viewer */}
            <div className="relative bg-slate-100 rounded-lg shadow-xl overflow-hidden flex items-center justify-center" style={{ minHeight: '600px' }}>
              {loading && (
                <div className="absolute inset-0 flex items-center justify-center bg-slate-100">
                  <div className="text-center">
                    <div className="w-12 h-12 border-4 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-slate-600">Loading slide deck...</p>
                  </div>
                </div>
              )}
              
              <Document
                file={currentDeck.pdfUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                loading=""
                className="flex items-center justify-center"
              >
                <Page
                  pageNumber={currentSlide}
                  width={1000}
                  renderTextLayer={true}
                  renderAnnotationLayer={true}
                  className="shadow-lg"
                />
              </Document>
              
              {/* Slide Counter Overlay */}
              {!loading && (
                <div className="absolute top-4 right-4 bg-black/70 text-white px-4 py-2 rounded-lg text-sm font-medium">
                  Slide {currentSlide} / {currentDeck.totalSlides}
                </div>
              )}
            </div>

            {/* Controls Bar */}
            <div className="bg-gray-900 px-6 py-4 flex items-center justify-between flex-wrap gap-4">
              {/* Navigation */}
              <div className="flex items-center gap-3">
                <button
                  onClick={prevSlide}
                  disabled={currentSlide === 1}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i>
                  Previous
                </button>
                <button
                  onClick={nextSlide}
                  disabled={currentSlide === currentDeck.totalSlides}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
                >
                  Next
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>

              {/* Slide Counter */}
              <div className="text-white font-medium">
                Slide {currentSlide} of {currentDeck.totalSlides}
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-3">
                <button
                  onClick={toggleFullscreen}
                  className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
                >
                  <i className={`${isFullscreen ? 'ri-fullscreen-exit-line' : 'ri-fullscreen-line'}`}></i>
                  {isFullscreen ? 'Exit' : 'Fullscreen'}
                </button>
                <a
                  href={currentDeck.pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-external-link-line"></i>
                  Open
                </a>
              </div>
            </div>
          </div>

          {/* Keyboard Shortcuts Info */}
          <div className="mt-6 bg-slate-50 rounded-lg p-4 border border-slate-200">
            <div className="flex items-center justify-center gap-8 text-sm text-slate-600 flex-wrap">
              <div className="flex items-center gap-2">
                <kbd className="px-2 py-1 bg-white border border-slate-300 rounded text-xs font-mono">←</kbd>
                <span>Previous</span>
              </div>
              <div className="flex items-center gap-2">
                <kbd className="px-2 py-1 bg-white border border-slate-300 rounded text-xs font-mono">→</kbd>
                <span>Next</span>
              </div>
              <div className="flex items-center gap-2">
                <kbd className="px-2 py-1 bg-white border border-slate-300 rounded text-xs font-mono">Esc</kbd>
                <span>Exit Fullscreen</span>
              </div>
            </div>
          </div>

          {/* Features List */}
          <div className="mt-8 grid md:grid-cols-2 gap-6">
            <div className="flex items-start gap-3 p-4 bg-white rounded-lg border border-slate-200">
              <div className="flex items-center justify-center w-10 h-10 bg-teal-100 rounded-lg flex-shrink-0">
                <i className="ri-keyboard-line text-xl text-teal-600"></i>
              </div>
              <div>
                <h5 className="font-semibold text-slate-900 mb-1">Keyboard Navigation</h5>
                <p className="text-sm text-slate-600">Use arrow keys to navigate slides quickly</p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 bg-white rounded-lg border border-slate-200">
              <div className="flex items-center justify-center w-10 h-10 bg-teal-100 rounded-lg flex-shrink-0">
                <i className="ri-fullscreen-line text-xl text-teal-600"></i>
              </div>
              <div>
                <h5 className="font-semibold text-slate-900 mb-1">Fullscreen Mode</h5>
                <p className="text-sm text-slate-600">Immersive viewing experience</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
